import { ChevronRightIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import ViewportChip from "@/components/domains/ViewportChip";
import type { CoverageChecklistItem, DomainTopOpenIssue } from "@/types";
import { evaluatedCoverageScope } from "@/utils/formatters";

type Props = {
  open: boolean;
  hostname: string;
  evaluatedPagesCount: number;
  evaluatedJourneysCount?: number;
  openIssueCount: number;
  topIssue: DomainTopOpenIssue | null;
  checklistItems: CoverageChecklistItem[];
  failedEvaluationNote: string | null;
  onDismiss: () => void;
  onViewIssues: (issueId?: string) => void;
};

export default function DomainEvaluationCompleteDialog({
  open,
  hostname,
  evaluatedPagesCount,
  evaluatedJourneysCount = 0,
  openIssueCount,
  topIssue,
  checklistItems,
  failedEvaluationNote,
  onDismiss,
  onViewIssues,
}: Props) {
  const [overlayRoot, setOverlayRoot] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setOverlayRoot(document.getElementById("layout-main-overlay"));
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onDismiss();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onDismiss]);

  // Lock main scroll while open. Do not restore scrollTop on unlock — view-issues owns position.
  useEffect(() => {
    if (!open) {
      return;
    }

    const main = document.querySelector("main[scroll-region]");
    if (!(main instanceof HTMLElement)) {
      return;
    }

    const previousOverflow = main.style.overflowY;
    main.style.overflowY = "hidden";

    return () => {
      main.style.overflowY = previousOverflow;
    };
  }, [open]);

  if (!open || !overlayRoot) {
    return null;
  }

  const issueLabel = openIssueCount === 1 ? "issue" : "issues";
  const remainingIssueCount = Math.max(openIssueCount - 1, 0);
  const hasFindings = Boolean(openIssueCount > 0 && topIssue?.title);
  const hasFailures = Boolean(failedEvaluationNote);
  const scope = evaluatedCoverageScope(evaluatedPagesCount, evaluatedJourneysCount);
  const pagesPhrase = scope ? `across ${scope}` : "like a visitor";

  const lead = hasFindings
    ? `We browsed ${hostname} ${pagesPhrase} and found ${openIssueCount} open usability ${issueLabel}. Highest priority first:`
    : hasFailures
      ? `We browsed ${hostname} ${pagesPhrase}, but something went wrong and the evaluation was incomplete.`
      : `We browsed ${hostname} ${pagesPhrase} and didn’t find any open usability issues.`;

  const footer =
    hasFindings && remainingIssueCount > 0
      ? `${remainingIssueCount} more open ${remainingIssueCount === 1 ? "issue is" : "issues are"} below.`
      : null;

  return createPortal(
    <div
      className="pointer-events-auto absolute inset-0 z-50 flex items-center justify-center overflow-hidden p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="eval-complete-backdrop absolute inset-0 bg-gray-900/20 backdrop-blur-sm" aria-hidden="true" />
      <div className="eval-complete-panel relative w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl ring-1 ring-black/5">
        <div className="flex items-start gap-4 bg-linear-to-br from-primary/15 via-primary/5 to-white px-6 pb-4 pt-6">
          <div className="flex min-w-0 flex-1 items-center space-x-5">
            <div className="eval-complete-icon flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-primary/10">
              <MagnifyingGlassIcon className="h-7 w-7 text-primary" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="eval-complete-eyebrow text-xs font-medium uppercase tracking-wider text-primary">
                Evaluation complete
              </p>
              <h2 className="eval-complete-headline mt-1 text-2xl font-semibold tracking-tight text-gray-900">
                We finished reviewing your site
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
            onClick={onDismiss}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <p className="eval-complete-lead text-sm leading-relaxed text-gray-600">{lead}</p>

          {hasFindings && topIssue ? (
            <button
              type="button"
              className="eval-complete-finding flex w-full cursor-pointer items-center gap-3 rounded-lg border border-amber-200/80 bg-amber-50/70 px-4 py-3 text-left transition hover:border-amber-300 hover:bg-amber-50"
              onClick={() => onViewIssues(topIssue.id)}
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800">
                    {topIssue.priority}
                  </span>
                  <span className="text-xs text-amber-800/70">Top finding</span>
                </div>
                <p className="mt-1.5 text-sm font-medium text-gray-900">{topIssue.title}</p>
              </div>
              <ChevronRightIcon className="h-5 w-5 shrink-0 text-amber-800/50" aria-hidden="true" />
            </button>
          ) : null}

          {failedEvaluationNote ? (
            <p className="text-sm leading-relaxed text-gray-600">{failedEvaluationNote}</p>
          ) : null}

          {checklistItems.length > 0 ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">What we checked</h3>
              <ul className="mt-2 space-y-1.5">
                {checklistItems.map((item) => (
                  <li
                    key={`${item.interaction_id}-${item.viewport}-${item.label}`}
                    className="flex items-center gap-2 text-sm text-gray-700"
                  >
                    <span className="text-primary" aria-hidden="true">
                      ✓
                    </span>
                    <span className="min-w-0 flex-1">{item.label}</span>
                    <ViewportChip viewport={item.viewport} />
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {footer ? <p className="eval-complete-footer text-sm text-gray-500">{footer}</p> : null}

          <button
            type="button"
            className="eval-complete-cta btn btn-primary w-full justify-center"
            onClick={() => onViewIssues()}
          >
            Check open issues
          </button>
        </div>
      </div>
    </div>,
    overlayRoot,
  );
}
